// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { ArrowTransferOutIcon, Button, DatePicker, InputAmount, notification } from '@gamesb42/ui-kit';
import { Form, Modal } from 'antd';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Flex from 'components/Flex';
import { formatAmount, formatDate, getCurrencyWithAmount } from 'helpers/formatters';
import useActivationOrder, { activationOrderDataType } from 'hooks/api/useActivationOrder';
import baseRequest from 'queries/baseRequest';
import urls from 'constants/urls';
import { GeneralEnum } from 'components/forms/ActivationOrderForm/data';
import useDefaultInterestGuard from 'components/forms/ActivationOrderForm/useDefaultInterestGuard';
import {
  formatDefaultInterestDate,
  getDefaultInterestOutstandingRows,
} from 'components/forms/ActivationOrderForm/defaultInterest';
import { N_A_VALUE } from 'components/common/GeneralView/Item';

import { amountWithCurr, formatInterest, replaceInString } from './helpers';
import s from './styles.module.scss';

const AvailableBalance = ({ status, balance }) => {
  if (status === 'default') {
    return (
      <Flex className={s.balanceRow} style={{ background: '#eaedf4' }} align="center" gap="32px">
        <div style={{ color: '#686F88' }}>Available balance:</div>
        <div>{balance}</div>
      </Flex>
    );
  }

  if (status === 'error') {
    return (
      <Flex className={s.balanceRow} style={{ background: '#FEECF1' }} align="center" gap="32px">
        <div style={{ color: '#686F88' }}>Available balance:</div>
        <div style={{ color: '#EC3E72' }}>
          {balance}
          {' Amount is not equal Available Balance'}
        </div>
      </Flex>
    );
  }

  return <></>;
};

export default function MainBlock({ activationOrderData }: { activationOrderData: activationOrderDataType }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [balanceRaw, setBalanceRaw] = useState('');
  const [balance, setBalance] = useState('');
  const [balanceStatus, setBalanceStatus] = useState('default');
  const history = useHistory();
  const [principalRepayment, setPrincipalRepayment] = useState(activationOrderData?.principal_repayment);
  const guardDefaultInterest = useDefaultInterestGuard();
  const { getActivationOrder } = useActivationOrder();
  const { t } = useTranslation();
  const defaultInterestOutstandingRows = getDefaultInterestOutstandingRows(activationOrderData);
  const isFunded = String(activationOrderData?.status).toUpperCase() === 'FUNDED';

  const openRepayment = () => {
    if (guardDefaultInterest(activationOrderData)) return;

    setModalOpen(true);
  };

  const createTransaction = async () => {
    const values = form.getFieldsValue();

    if (values.amount > balanceRaw) {
      setBalanceStatus('error');
      return;
    } else {
      setBalanceStatus('default');
    }

    try {
      const result = await baseRequest({
        path: `${process.env.REACT_APP_BASE_URL}/activation_order/future/${activationOrderData.id}/repayment`,
        options: {
          method: 'POST',
          body: {
            amount: values.amount,
            tx_date: values.tx_date.format('YYYY-MM-DD'),
          },
        },
      });

      if (result.detail) {
        notification.error({
          theme: 'dark',
          message: result.detail,
          placement: 'bottomLeft',
        });
      } else {
        setPrincipalRepayment(result?.principal_repayment);
        getActivationOrder({ id: activationOrderData.id as string });
        notification.success({
          theme: 'dark',
          message:
            Number(result?.principal_repayment) === 0
              ? 'Principal Repayment has been paid off. The AO status has been changed successfully'
              : 'Principal Repayment has been partially paid',
          placement: 'bottomLeft',
        });
      }

      setModalOpen(false);
    } catch (error) {
      console.error('Ошибка при создании транзакции:', error);
    }
  };

  const getBalance = async (id) => {
    try {
      const result = await baseRequest({
        path: `${process.env.REACT_APP_TS_URL}/v1/accounts/company-id/${id}`,
        options: {
          method: 'GET',
        },
      });

      const currencyCode = activationOrderData.currency === 'EUR' ? '978' : '840';
      const balanceVal = result.filter((el) => el.type === 'counterparty-client' && el.currency === currencyCode)[0]
        .balance;
      setBalanceRaw(balanceVal);

      if (balanceVal > 0) {
        setBalance(`${activationOrderData.currency === 'EUR' ? '€' : '$'}${balanceVal}`);
      } else {
        setBalance(
          `-${activationOrderData.currency === 'EUR' ? '€' : '$'}${formatAmount(balanceVal.toString().slice(1))}`,
        );
      }
    } catch (error) {
      console.error('Ошибка при получении баланса:', error);
    }
  };

  useEffect(() => {
    getBalance(activationOrderData?.company_id);
  }, []);

  useEffect(() => {
    setPrincipalRepayment(activationOrderData?.principal_repayment);
  }, [activationOrderData?.principal_repayment]);

  return (
    <>
      <Flex gap="32px">
        <Flex direction="column" className={s.block}>
          <Flex className={s.blockRow}>
            <div className={s.blockRowLabel}>Client:</div>
            <div
              onClick={() => history.push(`${urls.getCompany()}/${activationOrderData[GeneralEnum.COMPANY_ID]}`)}
              className={s.blockRowLinkValue}
            >
              {activationOrderData.company_name}
            </div>
          </Flex>
          <Flex className={s.blockRow}>
            <div className={s.blockRowLabel}>Agreement:</div>
            <div
              onClick={() =>
                history.push(`${urls.getCompany()}/general-docs/${activationOrderData[GeneralEnum.COMPANY_ID]}`)
              }
              className={s.blockRowLinkValue}
            >
              {activationOrderData.contract_number}
            </div>
          </Flex>
          <Flex className={s.blockRow}>
            <div className={s.blockRowLabel}>AO:</div>
            <div className={s.blockRowValue}>{activationOrderData?.name}</div>
          </Flex>
          <Flex className={s.blockRow}>
            <div className={s.blockRowLabel}>AO Date:</div>
            <div className={s.blockRowValue}>{formatDate(activationOrderData?.ao_document?.date as string)}</div>
          </Flex>
        </Flex>

        <Flex direction="column" className={s.block}>
          <Flex className={s.blockRow}>
            <div className={s.blockRowLabel}>Principal Repayment:</div>
            <Flex width="229px" align="center" justify="space-between" style={{ paddingRight: '16px' }}>
              <div className={s.blockRowValue}>
                {amountWithCurr(activationOrderData.currency, principalRepayment as string)}
              </div>
              {isFunded && <Button type="iconBtn" icon={<ArrowTransferOutIcon />} onClick={openRepayment} />}
            </Flex>
          </Flex>
          <Flex className={s.blockRow}>
            <div className={s.blockRowLabel}>VAT policy:</div>
            <div className={s.blockRowValue}>{replaceInString(activationOrderData.vat_policy as string)}</div>
          </Flex>
          <Flex className={s.blockRow}>
            <div className={s.blockRowLabel}>{t('forms.activationOrder.defaultInterestRate')}:</div>
            <div className={s.blockRowValue}>
              {formatInterest(replaceInString(activationOrderData?.penalty_policy as string))}
            </div>
          </Flex>
          {defaultInterestOutstandingRows.map(({ amount, currency, as_of_date: asOfDateValue }) => {
            const asOfDate = formatDefaultInterestDate(asOfDateValue);
            const hasAmount = amount !== null && amount !== undefined && amount !== '';

            return (
              <Flex
                key={`${currency || activationOrderData.currency}-${asOfDate || 'missing'}`}
                className={s.blockRow}
                style={{ height: 'auto', minHeight: '72px' }}
              >
                <div className={s.blockRowLabel} style={{ whiteSpace: 'pre-line' }}>
                  {t('forms.activationOrder.defaultInterestOutstanding', {
                    date: asOfDate || N_A_VALUE,
                  })}
                </div>
                <div className={s.blockRowValue}>
                  {hasAmount ? getCurrencyWithAmount(amount, currency || activationOrderData.currency) : N_A_VALUE}
                </div>
              </Flex>
            );
          })}
        </Flex>
      </Flex>

      <Flex gap="32px">
        <Flex direction="column" className={s.block}>
          <Flex className={s.blockRow}>
            <div className={s.blockRowLabel}>Purchase Price:</div>
            <div className={s.blockRowValue}>
              {amountWithCurr(activationOrderData.currency, activationOrderData.purchase_price as string)}
            </div>
          </Flex>
          <Flex className={s.blockRow}>
            <div className={s.blockRowLabel}>Purchased Receivables:</div>
            <div className={s.blockRowValue}>
              {amountWithCurr(activationOrderData.currency, activationOrderData.purchased_receivables as string)}
            </div>
          </Flex>
          <Flex className={s.blockRow}>
            <div className={s.blockRowLabel}>Purchase date:</div>
            <div className={s.blockRowValue}>{formatDate(activationOrderData?.purchase_date as string)}</div>
          </Flex>
        </Flex>

        <Flex direction="column" className={s.block}>
          <Flex className={s.blockRow}>
            <div className={s.blockRowLabel}>First Repayment date:</div>
            <div className={s.blockRowValue}>{formatDate(activationOrderData?.first_repayment_date as string)}</div>
          </Flex>
          <Flex className={s.blockRow}>
            <div className={s.blockRowLabel}>Final Repayment date:</div>
            <div className={s.blockRowValue}>{formatDate(activationOrderData?.final_repayment_date as string)}</div>
          </Flex>
        </Flex>
      </Flex>

      <Modal
        className={s.createT}
        title="Create transaction"
        open={modalOpen}
        okText="Create transaction"
        onOk={() => createTransaction()}
        onCancel={() => setModalOpen(false)}
        centered
      >
        <Form form={form}>
          <Flex direction="column">
            <Flex gap="32px">
              <Form.Item name="tx_date" rules={[{ required: true, message: 'Please input date' }]}>
                <DatePicker style={{ width: '343px' }} label="Date" size="large" format="DD.MM.YYYY" aria-required />
              </Form.Item>
              <Form.Item name="amount" rules={[{ required: true, message: 'Please input amount' }]}>
                <InputAmount
                  style={{ width: '343px' }}
                  label="Amount"
                  currency={activationOrderData?.currency!}
                  size="large"
                />
              </Form.Item>
            </Flex>
            <AvailableBalance status={balanceStatus} balance={balance} />
          </Flex>
        </Form>
      </Modal>
    </>
  );
}
